'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import { signalRSessizKayit } from '@/lib/signalrLogger'
import { getBrowserHubUrl } from '@/lib/pharmacyHubUrl'
import { getSignalRAccessToken, isSignalRUnauthorizedError } from '@/lib/signalrAccessToken'
import { refreshTokenSingleton } from '@/lib/tokenRefreshQueue'

export interface NetworkDmThreadDto {
  id: string
  peerTenantId: string
  peerUserId?: string | null
  peerGln?: string | null
  peerDisplayName: string
  peerUsername?: string | null
  peerCity?: string | null
  peerTown?: string | null
  localUserId?: string | null
  localUsername?: string | null
  isOversight?: boolean
  lastMessageAtUtc: string
  lastMessagePreview?: string | null
  lastSenderTenantId?: string | null
  lastSenderUserId?: string | null
  isOwnLastMessage: boolean
}

export interface NetworkDmSearchHit {
  kind: 'pharmacy' | 'user'
  peerTenantId: string
  peerUserId: string
  peerUsername?: string | null
  peerDisplayName: string
  peerGln?: string | null
  peerCity?: string | null
  peerTown?: string | null
  peerPersonName?: string | null
}

export interface NetworkDmMessageDto {
  id: string
  threadId: string
  senderTenantId: string
  senderUserId: string
  senderUsername?: string | null
  body: string
  createdAtUtc: string
  isOwn: boolean
}

function currentUserId(): string {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem('userId') ?? ''
}

function withOwnFlag(m: NetworkDmMessageDto): NetworkDmMessageDto {
  const me = currentUserId()
  return { ...m, isOwn: Boolean(me) && m.senderUserId === me }
}

export function usePharmacyNetworkThreads(enabled: boolean) {
  const [threads, setThreads] = useState<NetworkDmThreadDto[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    try {
      const res = await fetch('/api/opas/pharmacy-network/dm/threads', { credentials: 'include', cache: 'no-store' })
      const data = await res.json()
      if (res.ok) setThreads(data.items ?? [])
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    setLoading(true)
    void refresh()
  }, [enabled, refresh])

  const openThread = useCallback(async (peerTenantId?: string, gln?: string, peerUserId?: string) => {
    const res = await fetch('/api/opas/pharmacy-network/dm/threads', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ peerTenantId, gln, peerUserId }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Sohbet açılamadı')
    await refresh()
    return data.item as NetworkDmThreadDto
  }, [refresh])

  return { threads, loading, refresh, openThread }
}

export function usePharmacyNetworkDm(threadId: string | null, enabled: boolean) {
  const [messages, setMessages] = useState<NetworkDmMessageDto[]>([])
  const [loading, setLoading] = useState(false)
  const [connected, setConnected] = useState(false)
  const connRef = useRef<signalR.HubConnection | null>(null)

  const loadHistory = useCallback(async () => {
    if (!threadId || !enabled) return
    setLoading(true)
    try {
      const res = await fetch(
        `/api/opas/pharmacy-network/dm/threads/${encodeURIComponent(threadId)}/messages?take=80`,
        { credentials: 'include', cache: 'no-store' }
      )
      const data = await res.json()
      if (res.ok) setMessages((data.items ?? []).map((m: NetworkDmMessageDto) => withOwnFlag(m)))
    } finally {
      setLoading(false)
    }
  }, [threadId, enabled])

  useEffect(() => {
    if (enabled && threadId) {
      setLoading(true)
      void loadHistory()
    } else {
      setMessages([])
      setLoading(false)
    }
  }, [enabled, threadId, loadHistory])

  useEffect(() => {
    if (!enabled || !threadId) {
      setConnected(false)
      return
    }

    let cancelled = false

    async function connect() {
      try {
        const hubUrl = getBrowserHubUrl('/hubs/pharmacy-network')
        if (!hubUrl) return
        const initial = await getSignalRAccessToken()
        if (!initial) return

        let connection = connRef.current
        if (!connection) {
          connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, { accessTokenFactory: async () => (await getSignalRAccessToken()) ?? '' })
            .withAutomaticReconnect([0, 2000, 5000, 10000])
            .configureLogging(signalRSessizKayit)
            .build()

          connection.on('ReceiveNetworkDm', (payload: unknown) => {
            const raw = payload as NetworkDmMessageDto
            if (!raw?.id || raw.threadId !== threadId) return
            const m = withOwnFlag(raw)
            setMessages((prev) => (prev.some((p) => p.id === m.id) ? prev : [...prev, m]))
          })

          connRef.current = connection
        }

        try {
          if (connection.state === signalR.HubConnectionState.Disconnected) await connection.start()
        } catch (e) {
          if (isSignalRUnauthorizedError(e)) {
            await refreshTokenSingleton()
            if (connection.state === signalR.HubConnectionState.Disconnected) await connection.start()
          } else throw e
        }

        await connection.invoke('JoinDmThread', threadId)
        if (!cancelled) setConnected(true)
      } catch {
        if (!cancelled) setConnected(false)
      }
    }

    void connect()
    return () => {
      cancelled = true
      const c = connRef.current
      if (c && threadId) void c.invoke('LeaveDmThread', threadId).catch(() => {})
    }
  }, [enabled, threadId])

  const send = useCallback(async (body: string) => {
    if (!threadId) throw new Error('Sohbet seçili değil')
    const res = await fetch(
      `/api/opas/pharmacy-network/dm/threads/${encodeURIComponent(threadId)}/messages`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      }
    )
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Gönderilemedi')
    const item = data.item as NetworkDmMessageDto
    const flagged = withOwnFlag(item)
    setMessages((prev) => (prev.some((p) => p.id === flagged.id) ? prev : [...prev, flagged]))
    return flagged
  }, [threadId])

  return { messages, loading, connected, send, reload: loadHistory }
}

export function usePharmacyNetworkSettings(enabled: boolean) {
  const [dmEnabled, setDmEnabled] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    setLoading(true)
    void (async () => {
      try {
        const res = await fetch('/api/opas/pharmacy-network/settings', { credentials: 'include', cache: 'no-store' })
        const data = await res.json()
        if (res.ok) setDmEnabled(Boolean(data.dmEnabled))
      } finally {
        setLoading(false)
      }
    })()
  }, [enabled])

  const updateDmEnabled = useCallback(async (value: boolean) => {
    const res = await fetch('/api/opas/pharmacy-network/settings', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dmEnabled: value }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Kaydedilemedi')
    setDmEnabled(Boolean(data.dmEnabled))
  }, [])

  return { dmEnabled, loading, updateDmEnabled }
}

export interface NetworkFollowDto {
  tenantId: string
  gln?: string | null
  pharmacyDisplayName?: string | null
  city?: string | null
  town?: string | null
}

export interface NetworkFollowRequestIncomingDto {
  requesterTenantId: string
  requesterUsername?: string | null
  requesterPharmacyDisplayName?: string | null
  gln?: string | null
  city?: string | null
  town?: string | null
  createdAtUtc: string
}

export type FollowActionStatus = 'accepted' | 'pending'

export function usePharmacyNetworkFollows(enabled: boolean) {
  const [follows, setFollows] = useState<NetworkFollowDto[]>([])
  const [incomingRequests, setIncomingRequests] = useState<NetworkFollowRequestIncomingDto[]>([])
  const [outgoingPending, setOutgoingPending] = useState<NetworkFollowDto[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    try {
      const [followsRes, incomingRes, outgoingRes] = await Promise.all([
        fetch('/api/opas/pharmacy-network/follows', { credentials: 'include', cache: 'no-store' }),
        fetch('/api/opas/pharmacy-network/follow-requests/incoming', { credentials: 'include', cache: 'no-store' }),
        fetch('/api/opas/pharmacy-network/follow-requests/outgoing', { credentials: 'include', cache: 'no-store' }),
      ])
      const followsData = await followsRes.json()
      const incomingData = await incomingRes.json()
      const outgoingData = await outgoingRes.json()
      if (followsRes.ok) setFollows(followsData.items ?? [])
      if (incomingRes.ok) setIncomingRequests(incomingData.items ?? [])
      if (outgoingRes.ok) setOutgoingPending(outgoingData.items ?? [])
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    setLoading(true)
    void refresh()
  }, [enabled, refresh])

  const follow = useCallback(async (gln?: string, peerTenantId?: string) => {
    const res = await fetch('/api/opas/pharmacy-network/follows', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gln, peerTenantId }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'İstek gönderilemedi')
    await refresh()
    return { item: data.item as NetworkFollowDto, status: (data.status ?? 'pending') as FollowActionStatus }
  }, [refresh])

  const unfollow = useCallback(async (peerTenantId: string) => {
    const res = await fetch(`/api/opas/pharmacy-network/follows/${encodeURIComponent(peerTenantId)}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Takip bırakılamadı')
    setFollows((prev) => prev.filter((f) => f.tenantId !== peerTenantId))
    setOutgoingPending((prev) => prev.filter((f) => f.tenantId !== peerTenantId))
  }, [])

  const cancelOutgoing = useCallback(async (targetTenantId: string) => {
    const res = await fetch(`/api/opas/pharmacy-network/follow-requests/outgoing/${encodeURIComponent(targetTenantId)}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'İstek iptal edilemedi')
    setOutgoingPending((prev) => prev.filter((f) => f.tenantId !== targetTenantId))
  }, [])

  const acceptRequest = useCallback(async (requesterTenantId: string) => {
    const res = await fetch(`/api/opas/pharmacy-network/follow-requests/${encodeURIComponent(requesterTenantId)}/accept`, {
      method: 'POST',
      credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Kabul edilemedi')
    await refresh()
  }, [refresh])

  const rejectRequest = useCallback(async (requesterTenantId: string) => {
    const res = await fetch(`/api/opas/pharmacy-network/follow-requests/${encodeURIComponent(requesterTenantId)}/reject`, {
      method: 'POST',
      credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Reddedilemedi')
    setIncomingRequests((prev) => prev.filter((r) => r.requesterTenantId !== requesterTenantId))
  }, [])

  const isFollowingGln = useCallback(
    (gln: string) => follows.some((f) => f.gln === gln),
    [follows]
  )

  const isFollowingTenant = useCallback(
    (tenantId: string) => follows.some((f) => f.tenantId === tenantId),
    [follows]
  )

  const isPendingOutgoingTenant = useCallback(
    (tenantId: string) => outgoingPending.some((f) => f.tenantId === tenantId),
    [outgoingPending]
  )

  const isPendingOutgoingGln = useCallback(
    (gln: string) => outgoingPending.some((f) => f.gln === gln),
    [outgoingPending]
  )

  return {
    follows,
    incomingRequests,
    outgoingPending,
    loading,
    refresh,
    follow,
    unfollow,
    cancelOutgoing,
    acceptRequest,
    rejectRequest,
    isFollowingGln,
    isFollowingTenant,
    isPendingOutgoingTenant,
    isPendingOutgoingGln,
  }
}
